import { type DragEvent, useState } from 'react';
import { Link } from 'react-router';
import { Alert } from '../../components/ui';
import { t } from '../../i18n';
import { useMe } from '../../lib/auth';
import { useTaskStatus } from '../tasks/useTaskStatus';
import { formatDay, isOpen, type ProjectDetail, type ProjectTask, type TaskStatusOption } from './data';

/**
 * Tablero Kanban del proyecto (guía 7.2): una columna por estado de tarea. Las tarjetas son los pasos (y las fases
 * sin pasos): una fase con pasos es un contenedor y avanza con ellos. Se mueven arrastrando o con la lista de cada
 * tarjeta (teclado y lectores de pantalla).
 */
export function KanbanBoard({ project, statuses }: { project: ProjectDetail; statuses: readonly TaskStatusOption[] }) {
  const me = useMe();
  const status = useTaskStatus();
  const [over, setOver] = useState<string | null>(null);
  const parents = new Set(project.tasks.map((x) => x.parentId).filter(Boolean));
  const cards = project.tasks.filter((x) => !parents.has(x.id));
  const byId = new Map(project.tasks.map((x) => [x.id, x]));
  const canMove = (task: ProjectTask) => project.can.manage || task.assignees.some((a) => a.id === me.id);
  const openBlockers = (task: ProjectTask) => task.blockedBy.map((id) => byId.get(id)).filter((b): b is ProjectTask => !!b && isOpen(b.statusCategory));

  const drop = (e: DragEvent, statusId: string) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const task = byId.get(id);
    if (task && task.statusId !== statusId) void status.change(id, statusId);
  };

  return (
    <div className="space-y-3">
      {status.error && <Alert>{status.error}</Alert>}
      {status.blocked && (
        <Alert tone="warning">
          <p>{t('pr.bloqueada', { tarea: byId.get(status.blocked.taskId)?.title ?? '' })}</p>
          <ul className="mt-1 list-disc pl-5">
            {status.blocked.blockers.map((b) => (
              <li key={b.id}>
                {b.title} ({b.status})
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <div className="flex gap-3 overflow-x-auto pb-2" aria-label={t('pr.vista_tablero')}>
        {statuses.map((s) => {
          const column = cards.filter((c) => c.statusId === s.id).sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
          return (
            <section
              key={s.id}
              aria-label={s.name}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(s.id);
              }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => drop(e, s.id)}
              className={`flex w-64 shrink-0 flex-col rounded-lg border p-2 ${over === s.id ? 'border-marca bg-marca/5' : 'border-slate-200 bg-slate-50'}`}
            >
              <h3 className="mb-2 flex items-center justify-between px-1 text-xs font-semibold text-slate-600">
                {s.name}
                <span className="rounded bg-white px-1.5 text-slate-500">{column.length}</span>
              </h3>
              <ul className="space-y-2">
                {column.map((c) => {
                  const blockers = openBlockers(c);
                  const parent = c.parentId ? byId.get(c.parentId) : undefined;
                  return (
                    <li
                      key={c.id}
                      draggable={canMove(c) && !status.busy}
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', c.id)}
                      className={`rounded-md border bg-white p-2 text-sm shadow-sm ${canMove(c) ? 'cursor-grab' : ''} ${c.overdue ? 'border-red-300' : 'border-slate-200'}`}
                    >
                      <Link to={`/tareas/${c.id}`} className="font-medium text-slate-900 hover:underline">
                        {c.title}
                      </Link>
                      {parent && <p className="text-xs text-slate-500">{parent.title}</p>}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                        {c.assignees.length > 0 && <span>{c.assignees.map((a) => a.fullName).join(', ')}</span>}
                        {c.dueDate && <span className={c.overdue ? 'font-medium text-red-700' : ''}>{t('pr.vence_el', { fecha: formatDay(c.dueDate) })}</span>}
                        {blockers.length > 0 && <span className="font-medium text-amber-700">{t('pr.depende_de', { n: blockers.length })}</span>}
                      </div>
                      {canMove(c) && (
                        <label className="mt-1 block">
                          <span className="sr-only">{t('pr.mover', { tarea: c.title })}</span>
                          <select
                            className="w-full rounded border border-slate-200 bg-white px-1 py-0.5 text-xs text-slate-600"
                            value={c.statusId}
                            disabled={status.busy}
                            aria-label={t('pr.mover', { tarea: c.title })}
                            onChange={(e) => void status.change(c.id, e.target.value)}
                          >
                            {statuses.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
